import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { dashboardQueryOptions } from '@/api/resources/dashboard'
import { machinesQueryOptions } from '@/api/resources/machines'
import { DashboardCtaBanner } from '@/components/dashboard/dashboard-cta-banner'
import { DashboardQuickActions } from '@/components/dashboard/dashboard-quick-actions'
import { DashboardStatusCard } from '@/components/dashboard/dashboard-status-card'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { OrganisationFilter } from '@/components/layout/organisation-filter'
import { LocationMachineGroup } from '@/components/machines/location-machine-group'
import { MachineCard } from '@/components/machines/machine-card'
import { SalesOverviewPanel } from '@/components/machines/sales-overview-panel'
import { useOrganisationFilter } from '@/hooks/use-organisation-filter'
import { groupMachinesByLocation, hasMachineWarning } from '@/lib/machines'
import { useAuthStore } from '@/stores/auth-store'

export const Route = createFileRoute('/_app/')({
  head: () => ({ meta: [{ title: 'Dashboard - MyJunea' }] }),
  component: DashboardPage,
})

function DashboardPage(): React.ReactElement {
  const user = useAuthStore((s) => s.user)
  const organisationFilter = useOrganisationFilter()

  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery(
    dashboardQueryOptions(organisationFilter),
  )

  const { data: machines, isLoading: isMachinesLoading } = useQuery(
    machinesQueryOptions(organisationFilter),
  )

  const isLoading = isDashboardLoading || isMachinesLoading

  const locationGroups = machines !== undefined ? groupMachinesByLocation(machines) : []

  const statusCounts = computeStatusCounts(machines)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight">
            <span className="text-primary">Hey </span>
            {user?.first_name ?? 'there'}
          </h1>
          <p className="text-primary text-2xl font-extrabold tracking-tight">Overall summary</p>
        </div>
        <OrganisationFilter />
      </div>

      {isLoading && <LoadingSpinner />}

      {!isLoading && (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <SalesOverviewPanel
              type="transactions"
              months={dashboardData?.transactions.months ?? []}
              className="md:col-span-2"
            />
            <DashboardStatusCard
              operational={statusCounts.operational}
              needsAttention={statusCounts.needsAttention}
              offline={statusCounts.offline}
            />
          </div>

          <DashboardQuickActions />

          {locationGroups.length > 0 && (
            <div className="space-y-4">
              {locationGroups.map((group) => (
                <LocationMachineGroup
                  key={group.groupKey}
                  organisationName={group.organisationName}
                  locationName={group.locationName}
                  machineCount={group.machines.length}
                  hasWarning={group.machines.some(hasMachineWarning)}
                >
                  {group.machines.map((machine) => (
                    <Link
                      key={machine.code}
                      to="/machines/$machineCode"
                      params={{ machineCode: machine.code }}
                      className="focus-visible:ring-ring rounded-md transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:outline-none"
                    >
                      <MachineCard
                        name={machine.name}
                        code={machine.code}
                        type={machine.type}
                        typeName={machine.type_name}
                        status={machine.status}
                        statusCategory={machine.status_category}
                        stock={machine.stock}
                        capacity={machine.capacity}
                        emptySlots={machine.empty_slots}
                        className="h-full"
                      />
                    </Link>
                  ))}
                </LocationMachineGroup>
              ))}
            </div>
          )}

          <DashboardCtaBanner />
        </>
      )}
    </div>
  )
}

interface StatusCounts {
  operational: number
  needsAttention: number
  offline: number
}

function computeStatusCounts(
  machines: Array<{ status_category: 'operational' | 'needs-attention' | 'offline' }> | undefined,
): StatusCounts {
  if (machines === undefined) {
    return { operational: 0, needsAttention: 0, offline: 0 }
  }

  let operational = 0
  let needsAttention = 0
  let offline = 0

  for (const machine of machines) {
    if (machine.status_category === 'offline') {
      offline++
    } else if (machine.status_category === 'needs-attention') {
      needsAttention++
    } else {
      operational++
    }
  }

  return { operational, needsAttention, offline }
}
